import type { UIMessage, UIMessagePart } from 'ai';

/**
 * FRONT-2034: moved out of `message-bubble.tsx` verbatim (the FRONT-1685
 * docblock below is unchanged) so the phone surface (`pwa-message.tsx`) can
 * share the exact same ordering logic without importing the desktop bubble
 * module — `message-bubble.tsx` alone is a 2000+ line file with 34 imports
 * that pulls in cytoscape and friends, which the phone's critical path
 * deliberately never loads (see the comments at `pwa-message.tsx`, and the
 * identical precedent in `plan-status.ts` / `image-source.ts` /
 * `custom-tool-output-files.ts`). Dependency-free by the same rule those
 * modules follow: no React, no icons, no query hooks — the only import is
 * the `ai` package's own part/message types, which are erased at build time.
 *
 * `message-bubble.tsx` re-exports everything from here so its own call
 * sites, behaviour and tests are untouched.
 */

export type AnyToolPart = Extract<UIMessagePart<any, any>, { type: `tool-${string}` | 'dynamic-tool' }>;

export interface TextContentSegment {
  kind: 'text';
  text: string;
}

export interface ToolContentSegment {
  kind: 'tool';
  part: AnyToolPart;
  /**
   * 0-based, counting only tool parts — matches the numbering the old
   * `toolParts.filter(...)` array produced, so the existing React key
   * format (`${message.id}-tool-${toolPartIndex}-${info.id}`) is unchanged.
   */
  toolPartIndex: number;
}

export type ContentSegment = TextContentSegment | ToolContentSegment;

/**
 * FRONT-1685 (root cause A). The bubble used to partition `message.parts`
 * into by-type arrays (text / reasoning / tool) and render the whole tool
 * block before the whole text block — discarding the parts' original
 * stream order, so a tool card always landed above ALL of that turn's
 * prose regardless of where the model actually emitted the call. This
 * walks `parts` once, in order, and groups them into renderable segments
 * so a tool card renders exactly where it occurred, with prose split by a
 * tool call kept in its own segments rather than welded into one.
 *
 * Adjacent text parts merge into a single segment joined by a paragraph
 * break (root cause A2 — the old `.join('')` welded pre/post-tool-call
 * prose together with no separator, e.g. "...off.Waiting..."). Reasoning
 * and data-* parts are not part of this ordering — they're rendered
 * elsewhere (ReasoningBlock, SignalCards, the truth-guard/stall banners)
 * off the raw `message.parts` array, unaffected by this grouping.
 */
/**
 * FRONT-2601: a `data-continue-offer` part with `replacesText === true` means
 * the message's text is a server-authored boilerplate sentence that the
 * Continue card's own label replaces. Older messages lack the flag.
 */
export function hasTextReplacingContinueOffer(parts: UIMessage['parts']): boolean {
  return parts.some(
    (p) =>
      p.type === 'data-continue-offer' &&
      (p as { data?: { replacesText?: unknown } }).data?.replacesText === true,
  );
}

export function buildOrderedContentSegments(parts: UIMessage['parts']): ContentSegment[] {
  const dropText = hasTextReplacingContinueOffer(parts);
  const segments: ContentSegment[] = [];
  let toolPartIndex = 0;
  let pendingText: string[] = [];

  const flushText = () => {
    if (pendingText.length === 0) return;
    segments.push({ kind: 'text', text: pendingText.join('\n\n') });
    pendingText = [];
  };

  for (const part of parts) {
    if (part.type === 'text') {
      if (dropText) continue;
      pendingText.push(part.text);
      continue;
    }
    if (part.type === 'dynamic-tool' || (typeof part.type === 'string' && part.type.startsWith('tool-'))) {
      flushText();
      segments.push({ kind: 'tool', part: part as AnyToolPart, toolPartIndex });
      toolPartIndex += 1;
      continue;
    }
    // reasoning / data-* / other part types render elsewhere, or not at
    // all — they don't participate in this ordering.
  }
  flushText();
  return segments;
}
